import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Arc42Service } from '@core/api/generated/api/arc42.service';

/**
 * Guards the `arc42/:chapterSlug` route: the chapter must exist in the
 * currently selected project/branch. If it does not exist (e.g. because the
 * user switched to a branch where the chapter was not yet created, or the
 * chapter slug is simply invalid), the guard redirects to the architecture
 * overview (`architecture`) instead of letting the detail page render an error.
 *
 * `projectId` and `branchName` are parameters of the parent route
 * (`projects/:projectId/:branchName`). Because `paramsInheritanceStrategy`
 * is set to `'always'` (see app.config.ts), `route.paramMap` already
 * contains the inherited parent params, so no manual `route.parent`
 * traversal is necessary.
 *
 * This guard should be applied as a `canActivate` guard on the
 * `arc42/:chapterSlug` route, complementing `projectExistsGuard` and
 * `branchExistsGuard`, which already validated `projectId`/`branchName`.
 */
export const arc42ChapterExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const arc42Service = inject(Arc42Service);

  const projectId = route.paramMap.get('projectId');
  const branchName = route.queryParamMap.get('branchName');
  const number = +route.params['number'];

  // If any required param is missing, let other guards/logic handle it
  // (should practically not happen, since parent guards already ensure
  // projectId/branchName, and the route itself declares :chapterSlug).
  if (!projectId || !branchName || !number) {
    return true;
  }

  if (number < 1 || number > 12)
    return router.createUrlTree(['/projects', projectId, 'architecture'], {
      queryParams: { branchName },
    });

  return arc42Service.getArc42Chapter(projectId, branchName, number).pipe(
    map(() => true),
    catchError(() =>
      of(
        router.createUrlTree(['/projects', projectId, 'architecture'], {
          queryParams: { branchName },
        })
      )
    )
  );
};
